import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, ResponsiveContainer,
} from 'recharts';
import { LuChevronRight } from 'react-icons/lu';
import Card from '../../ui/Card/Card';
import CurrencyAmount from '../../ui/CurrencyAmount/CurrencyAmount';
import { getEnergyInsights } from '../../../api/energyInsightsApi';
import './InsightsSummaryCard.less';

/**
 * Compact dashboard tile — this-month kWh + what the driver paid + a
 * mini sparkline.
 *
 * Renders as an anchor (`Link`) so the entire card is tappable and
 * lands the user on `/energy-insights` for the full experience.
 *
 * On load failure the card silently keeps its previous state (or a
 * neutral shell) rather than showing an error — the full page has the
 * retryable error surface; the dashboard should degrade quietly.
 */
function InsightsSummaryCard({ currency = 'BBD' }) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getEnergyInsights();
      if (cancelled) return;
      if (result.ok) {
        setState({ loading: false, data: result });
      } else {
        setState({ loading: false, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const summary = state.data?.summary || null;
  const daily = state.data?.daily || [];
  const hasActivity = summary ? summary.sessionCount > 0 : false;

  // Reduce a full month down to weekly bars for the sparkline so a
  // 31-bar row doesn't turn to noise at ~24px height. Buckets align
  // to calendar weeks starting on the 1st.
  const sparkData = useMemo(() => {
    if (!daily.length) return [];
    const buckets = [];
    for (let i = 0; i < daily.length; i += 7) {
      const slice = daily.slice(i, i + 7);
      buckets.push({
        i,
        energyKWh: slice.reduce((acc, d) => acc + (d.energyKWh || 0), 0),
      });
    }
    return buckets;
  }, [daily]);

  return (
    <Card
      as={Link}
      to="/energy-insights"
      interactive
      className="gc-insights-summary"
      aria-label="Open Energy Insights"
    >
      <div className="gc-insights-summary-head">
        <div className="gc-insights-summary-eyebrow">This month</div>
        <div className="gc-insights-summary-title">Energy insights</div>
      </div>

      <div className="gc-insights-summary-body">
        <div className="gc-insights-summary-metrics">
          <div className="gc-insights-summary-primary">
            <span className="gc-insights-summary-primary-num">
              {summary ? summary.energyKWh.toFixed(1) : '—'}
            </span>
            <span className="gc-insights-summary-primary-unit">kWh</span>
          </div>
          <div className="gc-insights-summary-secondary">
            {summary ? (
              hasActivity ? (
                <>
                  <span>
                    {summary.sessionCount === 1
                      ? '1 session'
                      : `${summary.sessionCount} sessions`}
                  </span>
                  <span aria-hidden="true"> · </span>
                  {/* The driver's own card payments, not the value of
                      account-billed charging — say so. */}
                  <span>
                    You paid{' '}
                    <CurrencyAmount value={summary.totalCost} currency={currency} />
                  </span>
                </>
              ) : (
                <span>No charging activity yet</span>
              )
            ) : (
              <span>{state.loading ? 'Loading…' : '—'}</span>
            )}
          </div>
        </div>

        {sparkData.length > 0 && (
          <div className="gc-insights-summary-spark" aria-hidden="true">
            <ResponsiveContainer width="100%" height={44}>
              <BarChart data={sparkData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <Bar
                  dataKey="energyKWh"
                  fill="var(--gc-brand)"
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <span className="gc-insights-summary-chevron" aria-hidden="true">
        <LuChevronRight />
      </span>
    </Card>
  );
}

export default InsightsSummaryCard;
